import { Injectable } from '@angular/core';
import { Observable, of, Subject, tap, throwError } from 'rxjs';

interface CacheContent {
  expiry: number;
  value: any;
}

const DEFAULT_MAX_AGE = 8.64e7;

export const DATE_RANGE_FILTER_KEY = 'date-range-filter';

@Injectable({
  providedIn: 'root',
})
export class SharedCacheService {
  private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
  private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();

  get(key: string, fallback?: Observable<any>, maxAge = DEFAULT_MAX_AGE): Observable<any> {
    if (this.hasValidCachedValue(key)) {
      return of(this.cache.get(key)?.value);
    }
    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key)?.asObservable() || of();
    } else if (fallback && fallback instanceof Observable) {
      this.inFlightObservables.set(key, new Subject());
      return fallback.pipe(tap(value => this.set(key, value, maxAge)));
    } else {
      return throwError(() => new Error('Requested key is not available in Cache'));
    }
  }

  set(key: string, value: any, maxAge = DEFAULT_MAX_AGE): void {
    this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
    this.notifyInFlightObservers(key, value);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  private notifyInFlightObservers(key: string, value: any): void {
    if (this.inFlightObservables.has(key)) {
      const inFlight = this.inFlightObservables.get(key);
      if (inFlight) {
        inFlight.next(value);
        inFlight.complete();
      }
      this.inFlightObservables.delete(key);
    }
  }

  private hasValidCachedValue(key: string): boolean {
    if (this.has(key)) {
      if ((this.cache.get(key)?.expiry || Date.now() + 10000) < Date.now()) {
        this.cache.delete(key);
        return false;
      }
      return true;
    } else {
      return false;
    }
  }
}
